import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';

import {ToolsRibbonComponent} from 'src/components/tools-ribbon';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {RemoveAttendeesTool} from 'src/modules/bulk-update';

import {FlatButton} from '@pb/kit-component-button';
import {DropdownMenu, DropdownMenuItem} from '@pb/kit-component-dropdown-menu';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';


import {
    SELECTION_KEY,
    UserSearchDownloadTool,
    UserSearchEditColumns,
    userSearchResultResultSelector,
} from '../../users-groups-search-result';

import {GenerateLoginLinksTool} from './generate-login-links';

import type {ChildrenProps as UsersGroupsControllerProps} from '../../users-groups-search-result/users-groups-search-result.controller';
import type {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';

type Props = {
    accountId: number,
    getAllEntityIds: UsersGroupsControllerProps['getAllEntityIds'],
    place: string,
    adminId: number,
    isLoading: boolean,
    templates: UserSearchTemplate[],
    activeSearchTemplate: UserSearchTemplate,
    setActiveSearchTemplate: (actionProps: UserSearchTemplate) => Promise<string | null>,
    loadTemplates: () => void,
    loadActiveSearchTemplate: () => Promise<void>,
    refreshResults: () => void,
};

export const ConferenceUsersToolsRibbon = (props: Props) => {
    const {searchCriteriaDownload} = useEventTracking();

    const result = useSelector(userSearchResultResultSelector);
    const rows = useMemo(() => result?.data.map(({id}) => id) || [], [result]);
    const totalCount = useMemo(() => result?.totalCount || 0, [result]);

    const {hasAccess} = useAdminAccessRight();

    return (
        <ToolsRibbonComponent {...{
            selectionKey: SELECTION_KEY,
            rows,
            totalCount,
            extraTools: ids => (
                <Group>
                    <UserSearchEditColumns
                        place={props.place}
                        adminId={props.adminId}
                        isLoading={props.isLoading}
                        templates={props.templates}
                        activeSearchTemplate={props.activeSearchTemplate}
                        setActiveSearchTemplate={props.setActiveSearchTemplate}
                        loadTemplates={props.loadTemplates}
                        loadActiveSearchTemplate={props.loadActiveSearchTemplate}
                    />
                    <DropdownMenu
                        anchor={({positionRef, getAnchorProps, anchorRef}) => (
                            <FlatButton
                                disabled={!ids.length || !hasAccess(ACCESS_RIGHTS.CONFERENCE_VIEW)}
                                startIcon={<Icon type={Icons.DOWNLOAD}/>}
                                endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
                                {...getAnchorProps()}
                                ref={anchorRef}>
                                <b>Download</b>
                            </FlatButton>
                        )}>
                        {hasAccess(ACCESS_RIGHTS.USER_SCREEN_VIEW) ? (
                            <UserSearchDownloadTool
                                userIds={ids}
                                getSortedEntityIds={props.getAllEntityIds}>
                                {handler => (
                                    <DropdownMenuItem
                                        caption={props.activeSearchTemplate.templateName}
                                        onClick={() => {
                                            searchCriteriaDownload({
                                                viewType: `Conference Users - ${props.activeSearchTemplate.templateName}`,
                                            });
                                            handler();
                                        }}
                                    />
                                )}
                            </UserSearchDownloadTool>
                        ) : null}
                        {hasAccess(ACCESS_RIGHTS.CONFERENCE_EDIT) ? (
                            <GenerateLoginLinksTool
                                userIds={ids}
                                getSortedEntityIds={props.getAllEntityIds}>
                                {handler => (
                                    <DropdownMenuItem
                                        caption="Login Links"
                                        onClick={() => {
                                            searchCriteriaDownload({
                                                viewType: 'Conference Users - Login Links',
                                            });
                                            handler();
                                        }}
                                    />
                                )}
                            </GenerateLoginLinksTool>
                        ) : null}
                    </DropdownMenu>
                </Group>
            ),
            maxSelectedEntities: MAX_SELECTED_ENTITIES,
            getAllEntityIds: props.getAllEntityIds,
        }}>
            {(ids: Array<number | string>) => (
                <Group>
                    {hasAccess(ACCESS_RIGHTS.CONFERENCE_EDIT) ? (
                        <RemoveAttendeesTool
                            userIds={ids as number[]}
                            conferenceAccountId={props.accountId}
                            onDone={props.refreshResults}
                        />
                    ) : null}
                </Group>
            )}
        </ToolsRibbonComponent>
    );
};
